import React, { useState, useRef } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  CardTitle,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Container,
  Col,
} from "reactstrap";
import NotificationAlert from "react-notification-alert";
import { useNavigate } from "react-router-dom";
import { REGEX, ROLES, DEFAULT_ROUTES } from "components/Common/constants";
import { login } from "actions/public";

const Login = () => {
  const navigate = useNavigate();
  const notificationAlertRef = useRef(null);
  const [loginInfo, setLoginInfo] = useState({
    email: { value: "", error: "" },
    password: { value: "", error: "" },
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showLoaderOnClick, setShowLoaderOnClick] = useState(false);

  const notify = (type, message) => {
    const options = {
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    };
    notificationAlertRef.current.notificationAlert(options);
  };

  const handleChange = (name, value) => {
    setLoginInfo((prev) => ({
      ...prev,
      [name]: { ...prev[name], value: value.trim(), error: "" },
    }));
  };

  const preventSpace = (e) => {
    if (e.key === " ") {
      e.preventDefault(); // Prevent space from being typed
    }
  };

  const handleBlur = (name, value) => {
    switch (name) {
      case "email": {
        if (value === "") {
          setLoginInfo((prev) => ({
            ...prev,
            email: { ...prev.email, error: "Email is required" },
          }));
          return true;
        } else if (!REGEX.EMAIL_REGEX.test(value)) {
          setLoginInfo((prev) => ({
            ...prev,
            email: { ...prev.email, error: "Please enter a valid email" },
          }));
          return true;
        }
        break;
      }
      case "password": {
        if (value === "") {
          setLoginInfo((prev) => ({
            ...prev,
            password: { ...prev.password, error: "Password is required" },
          }));
          return true;
        }
        break;
      }
      default:
        return;
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (
      handleBlur("email", loginInfo.email.value) ||
      handleBlur("password", loginInfo.password.value)
    )
      return;

    try {
      setShowLoaderOnClick(true);
      const loginData = {
        email: loginInfo.email.value,
        password: loginInfo.password.value,
      };
      const res = await login(loginData);
      
      if (res.code === 200) {
        if (
          res.data.role !== ROLES.ADMINISTRATOR &&
          res.data.role !== ROLES.SPARE_PARTS
        ) {
          notify("danger", "Invalid credentials");
          return;
        }
        let userData = res.data;

        notify("success", res.message || "Login successful");
        localStorage.setItem("p", JSON.stringify(userData));

        const matchedKey = Object.keys(ROLES).find(
          (key) => ROLES[key] === userData.role
        );

        const redirect = DEFAULT_ROUTES[matchedKey] || "/admin/dashboard";
        setTimeout(() => {
          navigate(redirect);
        }, 1000);
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Sign in failed";
        notify("danger", message);
      }
    } catch (e) {
      notify("danger", "Sign in failed");
    } finally {
      setShowLoaderOnClick(false);
    }
  };

  return (
    <>
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>

      {/* use the original wrapper classname used by your theme */}
      <div className="login-page d-flex align-items-center justify-content-center min-vh-100">
        <Container>
          <Col lg="5" md="7" className="mx-auto">
            {/* use card-login + card-white so the theme SCSS targets this */}
            <Card className="card-login card-white border-0 shadow-lg">
              <CardHeader className="card-header bg-white text-center pb-0">
                <div className="logo-container mb-3">
                  {/* <img
                    src={require("assets/img/sample-icon.png")}
                    alt="Company Logo"
                  /> */}
                </div>
                <CardTitle tag="h3" className="text-default">
                  Sign In
                </CardTitle>
                <p className="text-muted small">
                  To manage schedules, products & customers
                </p>
              </CardHeader>

              <Form onSubmit={handleLogin}>
                <CardBody>
                  <FormGroup
                    className={`mt-4 ${
                      loginInfo.email.error ? "has-danger" : ""
                    } `}
                  >
                    <InputGroup>
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText>
                          <i className="tim-icons icon-email-85" />
                        </InputGroupText>
                      </InputGroupAddon>
                      <Input
                        name="email"
                        type="email"
                        placeholder="Email"
                        value={loginInfo.email.value}
                        onKeyDown={preventSpace}
                        onChange={(e) =>
                          handleChange(e.target.name, e.target.value)
                        }
                        onBlur={(e) =>
                          handleBlur(e.target.name, e.target.value)
                        }
                      />
                    </InputGroup>
                    {loginInfo.email.error && (
                      <small className="validation-error">
                        {loginInfo.email.error}
                      </small>
                    )}
                  </FormGroup>

                  <FormGroup
                    className={`mt-4 ${
                      loginInfo.password.error ? "has-danger" : ""
                    } `}
                  >
                    <InputGroup>
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText>
                          <i className="tim-icons icon-lock-circle" />
                        </InputGroupText>
                      </InputGroupAddon>

                      <Input
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Password"
                        value={loginInfo.password.value}
                        onKeyDown={preventSpace}
                        onChange={(e) =>
                          handleChange(e.target.name, e.target.value)
                        }
                        onBlur={(e) =>
                          handleBlur(e.target.name, e.target.value)
                        }
                      />
                      <InputGroupAddon addonType="append">
                        <InputGroupText
                          onClick={() => setShowPassword(!showPassword)}
                          className="pointer"
                        >
                          <i
                            className={
                              showPassword ? "fa fa-eye" : "fa fa-eye-slash"
                            }
                          />
                        </InputGroupText>
                      </InputGroupAddon>
                    </InputGroup>
                    {loginInfo.password.error && (
                      <small className="validation-error">
                        {loginInfo.password.error}
                      </small>
                    )}
                  </FormGroup>
                </CardBody>
                <div className="text-right mt-n4 mb-3 px-3">
                  <a
                    className="forget-password-link-text"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate("/user-reset-password");
                    }}
                    href="/user-reset-password"
                  >
                    Forget Password?
                  </a>
                </div>

                <CardFooter className="text-center">
                  {/* use theme default color (primary) */}
                  <Button
                    type="submit"
                    block
                    size="lg"
                    color="default"
                    className="mb-3"
                  >
                    {showLoaderOnClick ? (
                      <i className="fa fa-spinner fa-spin fa-lg text-white" />
                    ) : (
                      "Sign In"
                    )}
                  </Button>
                  <a
                    className="privacy-link-text"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate("/privacy-policy");
                    }}
                    href="/privacy-policy"
                  >
                    Privacy & Data Policy
                  </a>
                </CardFooter>
              </Form>
            </Card>
          </Col>
        </Container>
      </div>
    </>
  );
};

export default Login;
